import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import { cordovaExec, cordovaExecWithCallback } from '../CandyCaneService'
import { getZoneInfo, addZoneInfoChangeCallback } from '../ZoneModule'
import { IhmLoginTokenProvider } from '@api/utils/IhmLoginTokenProvider'
import { getUserInformation } from '../UserInfoService'
import { generateDeferredPromise } from '@utils/DeferredPomiseUtil'
import { PushNotificationConstants } from '@utils/Constants/PushNotificationConstants'
import { addNotification } from '@services/notifications/NotificationTaskService'
import _ from 'lodash'

/**
 * Call actiavte to initialize Push Notification Service.
 * Comment Push Notification Service activate before enabling in Atbase.
 */
export const activate = () => {
    const deferred = generateDeferredPromise()
    if (EnvironmentDetail.isAndroid()) {
        console.log('ATBase: Android Activating PushNotification Service')
        clearNotifications()
            .then(initializeZoneListener)
            .then(registerResumeEventListener)
            .then(registerNotificationCallback)
            .catch((error) => {
                console.error('ATBase: PushNotification: callback registration failed', error)
            })
            .finally(() => {
                deferred.resolve({})
            })
    } else {
        console.log('ATBase: PushNotificationService: attempt to activate at unsupported environment.')
        deferred.resolve({})
    }
    return deferred.promise
}

/**
 * Clears notifications during initialization.
 */
const clearNotifications = () => {
    console.log('ATBase: PushNotification: clearing notifications')
    return cordovaExec(PushNotificationConstants.NOTIFICATION_PLUGIN, PushNotificationConstants.ACTION_DELETE_NOTIFICATIONS, [])
}

const initializeZoneListener = () => {
    console.log('ATBase: PushNotification initializeZoneListener')
    addZoneInfoChangeCallback(zoneListener)

    const currentZoneInfo = getZoneInfo()
    if (currentZoneInfo.zone) {
        zoneListener(currentZoneInfo)
    }
}

const zoneListener = (zoneInfo) => {
    console.log('ATBase: PushNotification zoneListener')
    setZoneInfo(zoneInfo, 'global')
        .then(() => {
            console.info('ATBase: PushNotificationService: set zoneInfo to plugin successful')
        })
        .catch((error) => {
            console.error('ATBase: PushNotificaitonservice: set zoneInfo to plugin failed.', error)
        })
}

/**
 * Sets zoneInfo in notification plugin.
 */
export const setZoneInfo = async (zonInfo: any, contextType: any) => {
    console.log('ATBase: PushNotification setZoneInfo')
    const deferred = generateDeferredPromise()
    if (EnvironmentDetail.isAndroid()) {
        const zoneArgs = {
            contextType,
            contextKey: PushNotificationConstants.KEY_ZONE,
            contextValue: zonInfo.zoneName,
        }
        const subZoneArgs = {
            contextType,
            contextKey: PushNotificationConstants.KEY_SUB_ZONE,
            contextValue: zonInfo.subzoneName,
        }

        try {
            console.log('ATBase: PushNotification cordovaExec NOTIFICATION_PLUGIN')
            await cordovaExec(PushNotificationConstants.NOTIFICATION_PLUGIN, PushNotificationConstants.ACTION_SET_CONTEXT_KEY, [zoneArgs])
            return await cordovaExec(PushNotificationConstants.NOTIFICATION_PLUGIN, PushNotificationConstants.ACTION_SET_CONTEXT_KEY, [subZoneArgs])
        } catch (error) {
            console.error('ATBase: PushNotificationService: setting zoneInfo to plugin failed.', error)
            deferred.reject(error)
        }
    } else {
        console.log('ATBase: PushNotificationService: attempt to setZoneInfo at unsupported environment.')
        deferred.resolve({})
    }
    return deferred.promise
}

/**
 * Registers resume event handler.
 * Utility: When application comes from background to foreground, resume event is fired.
 * This registered handler will check and process stored notification in candyCane.
 */
const registerResumeEventListener = () => {
    console.log('ATBase PushNotification registerResumeEventListener')
    document.addEventListener('resume', resumeEventHandler, false)
}

/**
 * Called when resume event is fired.
 * If resume event fired and user is loggedIn, it fetches and process notification.
 */
const resumeEventHandler = () => {
    console.log('ATBase PushNotification Service: resume event handler triggered.')
    IhmLoginTokenProvider.isTokenSet()
        .then((isTokenSet) => {
            if (isTokenSet) {
                cordovaExec(PushNotificationConstants.NOTIFICATION_PLUGIN, PushNotificationConstants.ACTION_GET_NOTIFICATIONS, [{ deleteNotifications: true }])
                    .then(processNotifications)
                    .catch((error) => {
                        console.error('ATBase: PushNotificationService: getNotifications action failed. ', error)
                    })
            }
        })
}

/**
 * Process and display notifications received from plugin.
 */
const processNotifications = async (notifications: any) => {
    console.log('ATBase PushNotification Service: Notifications received from plugin: ' + JSON.stringify(notifications))

    const filteredNotifications = filterNotifications(notifications)
    const notification = chooseNotification(filteredNotifications)
    console.log('ATBase PushNotification Service: notification to display: ' + JSON.stringify(notification))

    if (_.isUndefined(notification) || _.isNull(notification)) {
        console.log('ATBase PushNotification Service: notification not present.')
        return
    }

    const messageData = JSON.parse(notification.messageData)
    const payload = JSON.parse(messageData.payload)
    // Temporary workaround: IhmLibrarianService needs to update the notificationType that they create:
    // https://t.corp.amazon.com/BAKU-1691
    if (payload.notificationId.startsWith('IhmLibrarianService')) {
        payload.notificationType = 'OFS'
    }
    const deferred = generateDeferredPromise()
    try {
        const userAlias = await getUserAlias()
        let associateIds
        let associateRoles
        if (payload.allowedRoles) {
            associateRoles = payload.allowedRoles.filter((role) => {
                return role.roleSource === 'ASSOCIATE'
            })
        }
        if (associateRoles) {
            associateIds = _.map(associateRoles, (role) => {
                return role.roleName
            })
        }
        addNotification(payload)
    } catch (error) {
        console.log('ATBase: PushNotification Service: unable to process with error', error)
    }
    return deferred.promise
}

/**
 * Filter the list of notifications using each notification's filtering conditions.
 */
const filterNotifications = (notifications: []) => {
    // TODO: enable filter logic here.
    return notifications
}

const chooseNotification = (notifications) => {
    return notifications.length > 0 ? notifications[0] : undefined
}
/**
 * Fetches userAlias from UserInfoService.
 * @returns {string} userAlias: Returns userAlias.
 */
const getUserAlias = async () => {
    return getUserInformation()
        .then((response) => {
            console.log('ATBase PushNotification Service: getUserInformation response from UserInfoService: ', response)
            if (response.body && response.body.userAlias) {
                return response.body.userAlias
            }
            if (response || response.userAlias) {
                return response.userAlias ? response.userAlias : response
            }
            return Promise.reject('ATBase PushNotificationService: userAlias not present.')
        })
        .catch((error) => {
            console.error('ATBase: PushNotificationService: unable to fetch userInfo. ', error)
            return Promise.reject(error)
        })
}

const registerNotificationCallback = () => {
    console.log('ATBase: PushNotification registerNotificationCallback')
    return cordovaExecWithCallback(onNotificationReceivedHandler, null, PushNotificationConstants.NOTIFICATION_PLUGIN, PushNotificationConstants.ON_NOTIFICATION_RECEIVED, [])
}

const onNotificationReceivedHandler = () => {
    console.log('ATBase PushNotification Service: onNotificationReceivedHandler triggered. ')
    cordovaExec(PushNotificationConstants.NOTIFICATION_PLUGIN, PushNotificationConstants.ACTION_GET_NOTIFICATIONS, [{ deleteNotifications: true }])
        .then(processNotifications)
        .catch((error) => {
            console.error('ATBase: PushNotificationService: getNotifications action failed. ', error)
        })
}

/**
 * Sets auth status in notification plugin.
 */
export const setLoginStatus = (loginStatus: string, contextType: string) => {
    if (EnvironmentDetail.isAndroid()) {
        const args = {
            contextType,
            contextKey: PushNotificationConstants.KEY_LOGIN_STATUS,
            contextValue: loginStatus,
        }
        return cordovaExec(PushNotificationConstants.NOTIFICATION_PLUGIN, PushNotificationConstants.ACTION_SET_CONTEXT_KEY, [args])
    }
}
